import { action, Action, thunk, Thunk, computed, Computed } from 'easy-peasy';

import { Firebase } from 'services';
import { createRateContract } from 'services/firebase/rateContracts';
import {
  ICurrency,
  IRateContract,
  IResponse,
  IContractInput,
  Nullable,
} from 'types';

export interface RateContractsStateModel {
  rateContracts: IRateContract[];
  rateContractsByCurrencyPair: Computed<
    RateContractsStateModel,
    (
      sellCurrencyCode: ICurrency['code'],
      buyCurrencyCode: ICurrency['code']
    ) => IRateContract[]
  >;
  rateContractsByCurrency: Computed<
    RateContractsStateModel,
    (buyCurrencyCode: ICurrency['code']) => IRateContract[]
  >;
  rateContractById: Computed<
    RateContractsStateModel,
    (id: Nullable<IRateContract['id']>) => IRateContract | null
  >;
  setState: Action<RateContractsStateModel, [string, any]>;
  subscribeToRateContracts: Thunk<
    RateContractsStateModel,
    Omit<Firebase.SubscribeToTransfersParams, 'callback'>,
    null,
    object,
    (() => void) | undefined
  >;
  createRateContract: Thunk<
    RateContractsStateModel,
    IContractInput,
    null,
    object,
    Promise<IResponse | undefined>
  >;

  getRateContractCancelValue: Thunk<
    RateContractsStateModel,
    // TODO: this interface should got to rate contracts
    Firebase.Invoices.GetContractRateCancelValueParams
  >;
}

export const RateContractsState: RateContractsStateModel = {
  rateContracts: [],
  rateContractsByCurrencyPair: computed(
    [(state) => state.rateContracts],
    (rateContracts) => (sellCurrencyCode, buyCurrencyCode) =>
      rateContracts.filter(
        ({ sellCurrency, buyCurrency }) =>
          sellCurrency === sellCurrencyCode && buyCurrency === buyCurrencyCode
      ) || []
  ),
  rateContractsByCurrency: computed(
    [(state) => state.rateContracts],
    (rateContracts) => (buyCurrencyCode) =>
      rateContracts.filter(
        (contract) => contract.buyCurrency === buyCurrencyCode
      ) || []
  ),
  rateContractById: computed(
    [(state) => state.rateContracts],
    (rateContracts) => (id) =>
      rateContracts.find((item) => item.id === id) || null
  ),
  setState: action((state, payload) => {
    const [prop, to] = payload;
    state[prop] = to;
  }),
  subscribeToRateContracts: thunk(({ setState }, payload) => {
    const subscriber = Firebase.subscribeToRateContracts({
      ...payload,
      callback: (rateContracts) => setState(['rateContracts', rateContracts]),
    });

    return subscriber;
  }),
  createRateContract: thunk(async (actions, payload) => {
    const data = await createRateContract(payload);

    return data;
  }),
  getRateContractCancelValue: thunk(async (actions, payload) => {
    try {
      const cancelValue = await Firebase.Invoices.getContractRateCancelValue(
        payload
      );

      return cancelValue;
    } catch (error) {
      console.log(error);
    }
  }),
};
